import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import StyledCheckbox from '../../app/components/checkbox/StyledCheckbox';
import CrossIcon from '../../app/components/svgIcons/CrossIcon';
import WelcomeBotIcon from '../../app/components/svgIcons/WelcomeBotIcon';
import { StyledLabel, Wrap } from '../../assets/styledComponents/Global';
import {
  getPermissionDisplayLabel,
  PERMISSION_DENIED_MESSAGE,
} from '../constants/permissions';
import { resetPermissionDeniedBatch } from '../actions/permissionDenied';
import { normalizePermissions, saveSuppressionPreference } from '../modal-suppression';
import {
  closePermissionDenied,
  selectPermissionDeniedModal,
} from '../store/permissionGateSlice';

export default function PermissionDeniedModal() {
  const dispatch = useDispatch();
  const th = useTheme();
  const [suppressFutureWarnings, setSuppressFutureWarnings] = useState(false);
  const {
    deniedModalOpen,
    message,
    requiredPermission,
    blockedAttemptCount = 0,
    blockedPermissions = [],
  } = useSelector(selectPermissionDeniedModal);

  const subtitle = message || PERMISSION_DENIED_MESSAGE;
  const perms = useMemo(() => {
    if (blockedPermissions?.length) return blockedPermissions;
    if (requiredPermission) return [requiredPermission];
    return [];
  }, [blockedPermissions, requiredPermission]);
  const border = th?.inputBorder ?? '#ebeaeb';
  const text = th?.textColor ?? '#585558';
  const modalBg = th?.modalInnerBg ?? th?.bodyBg ?? '#ffffff';
  const sectionBg = th?.white ?? '#ffffff';
  const itemBg = th?.darkTileBg ?? th?.lightGray ?? '#fcfcfc';
  const warningBg = th?.toastErrorBg ?? '#fff8f7';
  const warningText = th?.toastErrorTextColor ?? text;
  const warningBorder = th?.toastErrorBorderColor ?? '#f5b8b4';
  const badgeBg = th?.statusTagOverDue ?? '#ffebee';
  const badgeText = th?.toastErrorBorderColor ?? '#e53935';

  useEffect(() => {
    if (!deniedModalOpen) setSuppressFutureWarnings(false);
  }, [deniedModalOpen]);

  const handleClose = useCallback(() => {
    if (suppressFutureWarnings && perms.length) {
      saveSuppressionPreference(normalizePermissions(perms));
    }
    setSuppressFutureWarnings(false);
    resetPermissionDeniedBatch();
    dispatch(closePermissionDenied());
  }, [dispatch, perms, suppressFutureWarnings]);

  return (
    <Modal
      show={deniedModalOpen}
      backdrop="static"
      centered
      onHide={handleClose}
      dialogClassName="permission-denied-modal-dialog"
      contentClassName="rounded-3 overflow-hidden"
    >
      <style>{`
        .permission-denied-modal-dialog { max-width: 560px; }
        .permission-denied-modal-dialog .modal-content {
          border: 1px solid ${border};
        }
      `}</style>
      <Modal.Body
        className="d-flex flex-column rounded-3 align-items-center p-4"
        style={{ backgroundColor: modalBg }}
      >
        <Wrap className="d-flex justify-content-end w-100">
          <CrossIcon
            className="ms-auto cursor-pointer"
            size={19}
            color={th?.headingColor}
            onClick={handleClose}
          />
        </Wrap>
        <div className="d-flex justify-content-center mb-3">
          <WelcomeBotIcon size={100} />
        </div>
        <StyledLabel weight={600} size="24px" className="mt-1">
          Permission required
        </StyledLabel>
        <StyledLabel
          weight={400}
          size="16px"
          color={text}
          className="text-center mt-1"
          style={{ maxWidth: 360 }}
        >
          {subtitle}
        </StyledLabel>
        {blockedAttemptCount > 1 ? (
          <StyledLabel
            weight={400}
            size="14px"
            color={text}
            className="text-center mt-2"
          >
            {blockedAttemptCount} blocked requests (including simultaneous calls).
          </StyledLabel>
        ) : null}

        <div className="w-100 mt-3">
          {perms.length > 0 ? (
            <div
              className="rounded-3 p-3 mb-3"
              style={{ border: `1px solid ${border}`, background: sectionBg }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <StyledLabel
                  weight={600}
                  size="11px"
                  color={th?.inputPlaceHolder ?? '#9a9fa5'}
                  className="text-uppercase"
                  style={{ letterSpacing: '0.06em' }}
                >
                  Missing permission
                </StyledLabel>
                <span
                  className="small fw-semibold rounded-pill px-2 py-1"
                  style={{ color: badgeText, background: badgeBg }}
                >
                  {perms.length} required
                </span>
              </div>
              {perms.map((p) => (
                <div
                  key={p}
                  className="d-flex gap-2 align-items-start rounded-2 p-2 mt-2"
                  style={{
                    border: `1px solid ${border}`,
                    background: itemBg,
                  }}
                >
                  <span
                    className="rounded-circle flex-shrink-0 mt-1"
                    style={{
                      width: 8,
                      height: 8,
                      background: '#e53935',
                    }}
                  />
                  <span
                    className="small fw-semibold"
                    style={{ color: th?.headingColor, wordBreak: 'break-word' }}
                    title={p}
                  >
                    {getPermissionDisplayLabel(p)}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          <div
            className="rounded-2 p-3 mb-3 small"
            style={{
              border: `1px dashed ${warningBorder}`,
              background: warningBg,
              color: warningText,
            }}
          >
            <strong style={{ color: th?.headingColor }}>Need access?</strong> Reach
            out to your workspace administrator to request the required permission.
          </div>

          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
            {perms.length > 0 ? (
              <StyledCheckbox
                value={suppressFutureWarnings}
                onChange={(e) => setSuppressFutureWarnings(e.target.checked)}
                label="Don't show this permission warning again for these permissions."
                labelSize="12px"
                align="align-items-center"
                className="flex-grow-1"
              />
            ) : (
              <span />
            )}
            <StyledButton
              variant="primary"
              className="fw-bold flex-shrink-0"
              onClick={handleClose}
            >
              Got it
            </StyledButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
